import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import HelpErrorTextsTemplate from '../../../components/HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import RequiredTemplate from '../../../components/RequiredTemplate/RequiredTemplate';

import { PlusCircleOutlineIcon } from '../../../images/shapes';

import { useFieldContext, useFormContext } from '../../form';
import { useFieldErrors } from '../../useFieldErrors';

const OptionsField = ({
  label = '',
  useOptionsWithLabels = false,
  additionalClasses = '',
  helpText = '',
  required = false,
  disabled = false,
  readonly = false,
  testId = '',
}) => {
  const field = useFieldContext();
  const form = useFormContext();
  const [focusIdx, setFocusIdx] = useState(null);

  const errors = useFieldErrors();

  const value = useMemo(() => field.state.value || [], [field.state.value]);

  const onBlur = useCallback(() => field.handleBlur(), [field]);

  const handleAddOption = useCallback(() => {
    field.pushValue(
      useOptionsWithLabels
        ? {
            value: '',
            label: '',
          }
        : '',
    );
    onBlur();
  }, [field, onBlur, useOptionsWithLabels]);

  const handleOrderUp = useCallback(
    (idx) => {
      field.swapValues(idx - 1, idx);
    },
    [field],
  );

  const handleOrderDown = useCallback(
    (idx) => {
      field.swapValues(idx + 1, idx);
    },
    [field],
  );

  const handleDeleteOption = useCallback(
    async (idx) => {
      field.removeValue(idx);
    },
    [field],
  );

  const handleKeyDownAction = useCallback(
    (e, option, currentValue, idx) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddOption();
        setFocusIdx(idx + 1);
      } else if (e.key === 'Backspace' && option === '') {
        e.preventDefault();
        handleDeleteOption(idx);
        if (currentValue.length > 1) setFocusIdx(idx === 0 ? idx + 1 : idx - 1);
      } else if (e.key === 'ArrowUp' && idx > 0) {
        e.preventDefault();
        handleOrderUp(idx);
      } else if (e.key === 'ArrowDown' && idx < currentValue.length - 1) {
        e.preventDefault();
        handleOrderDown(idx);
      }
    },
    [handleAddOption, handleDeleteOption, handleOrderDown, handleOrderUp],
  );

  return (
    <div
      className={twMerge(
        'w-full',
        'flex',
        'flex-col',
        'relative',
        additionalClasses,
      )}
    >
      {label && (
        <label className="text-sm text-slate-400 dark:text-gray-200 mb-1">
          {label}
          {required && <RequiredTemplate />}
        </label>
      )}
      {value && value.length > 0 && (
        <div className="mb-2 space-y-2">
          {value.map((option, idx, currentValue) => {
            const keyValue = idx;
            const fieldName = `${field.name}[${idx}]`;
            return (
              <form.AppField key={fieldName} name={fieldName}>
                {(subField) => (
                  <subField.OptionField
                    key={keyValue}
                    option={option}
                    idx={idx}
                    currentValue={currentValue}
                    focusIdx={focusIdx}
                    onKeyDown={handleKeyDownAction}
                    onUp={handleOrderUp}
                    onDown={handleOrderDown}
                    onDelete={handleDeleteOption}
                    disabled={disabled}
                    readonly={readonly}
                    useOptionsWithLabels={useOptionsWithLabels}
                    testId={testId}
                  />
                )}
              </form.AppField>
            );
          })}
        </div>
      )}
      <Button
        onClick={handleAddOption}
        buttonColor="blueBordered"
        buttonSize="sm"
        iconImage={<PlusCircleOutlineIcon className="w-4" />}
        additionalClasses="w-fit"
        disabled={disabled || readonly}
        {...getTestProps(testId, 'add-option', 'testId')}
      />
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={errors}
        testId={testId}
      />
    </div>
  );
};

export default OptionsField;

OptionsField.propTypes = {
  /**
   * Array helpers from formik array field
   */
  arrayHelpers: PropTypes.object.isRequired,
  /**
   * Options field label
   */
  label: PropTypes.string,
  /**
   * Errors for options
   */
  error: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  /**
   * If options should be rendered as key-value pair (default it is an array of string)
   */
  useOptionsWithLabels: PropTypes.bool,
  /**
   * Component test id
   */
  testId: PropTypes.string,
  /**
   * Additional container classes
   */
  additionalClasses: PropTypes.string,
  /**
   * Help text under block input
   */
  helpText: PropTypes.node,
  /**
   * If field is required
   */
  required: PropTypes.bool,
  /**
   * If field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * If field is readonly (non interactive but without disabled classes)
   */
  readonly: PropTypes.bool,
};
